import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { Head, useForm } from '@inertiajs/react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import {
  SCHOOL_FEATURE_MAP,
  resolveFeatureIcon,
  type CustomSchoolFeature,
} from '@/lib/schoolFeatures'
import type { LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import PublicHeader from '@/components/public/PublicHeader'
import Breadcrumb from '@/components/public/Breadcrumb'
import {
  APP_STORE_URL,
  EXPLORE_PATH,
  PLACEHOLDER_RATING,
  PLAY_STORE_URL,
  PUBLIC_CONTAINER,
} from '@/components/public/publicShell'
import { useFavorite } from '@/hooks/useFavorite'
import { whatsappLink } from '@/lib/publicText'
import { trackWhatsAppClick } from '@/lib/analytics'
import WhatsAppLink from '@/components/public/WhatsAppLink'
import BewaveFooter from '@/components/public/BewaveFooter'
import VideoDoCriador from '@/components/public/VideoDoCriador'
import { useIsMobile } from '@/hooks/use-mobile'
import type { FooterColumn, FooterLink } from '@/components/public/PublicFooter'
import type { CityOption } from '@/types/publicSchool'
import {
  ChevronLeft,
  Star,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  MapPin,
  Phone,
  Navigation,
  Check,
  ArrowRight,
  CheckCircle2,
  Smartphone,
  Share,
  Heart,
  Instagram,
  Award,
  Users,
  CalendarDays,
  Waves,
  Link2,
  Mail,
  Facebook,
  Twitter,
  MessageCircle,
  MoreHorizontal,
  Calendar,
  Ticket,
  Sparkles,
} from 'lucide-react'

/* ─────────────────────────── Tipos ─────────────────────────── */

interface Package {
  id: number
  name: string
  description: string | null
  lessons_count: number
  validity_days: number | null
  price: number | null
}

interface TrialPackage {
  id: number
  name: string
  description: string | null
  lessons_count: number
  validity_days: number | null
  free: boolean
  price: number | null
}

interface Teacher {
  id: number
  name: string
  avatar_url: string | null
  specialty?: string | null
  years_experience?: number | null
  bio?: string | null
}

interface OperatingHours {
  days?: (string | number)[]
  morning?: { start?: string; end?: string } | null
  afternoon?: { start?: string; end?: string } | null
}

/** Um produto ou serviço do catálogo do negócio. */
interface CatalogEntry {
  id: number
  kind: 'servico' | 'produto'
  name: string
  description: string | null
  /** Em reais. `null` é "sob consulta". */
  price: number | null
  unit: string | null
  photos: string[]
}

/** Quem está por trás da página — o perfil do criador. */
interface Host {
  name: string
  kind: 'pessoa' | 'empresa'
  headline: string | null
  bio: string | null
  avatar_url: string | null
}

interface EventItem {
  id: number
  title: string
  type: string
  date: string
  location: string | null
  price: number | null
  banner_url: string | null
}

/**
 * Redes sociais informadas — hoje só as páginas de criador preenchem isto
 * (o cadastro de escola não tem esses campos). Cada uma pode faltar.
 */
interface SocialLinks {
  website?: string | null
  instagram?: string | null
  tiktok?: string | null
  youtube?: string | null
}

/**
 * Um quadro da vitrine do topo: uma foto ou o vídeo em destaque.
 *
 * Só existe DENTRO da vitrine. A prop `gallery` continua sendo `string[]` de
 * URLs, porque é assim que o resto da landing (compartilhar, og:image, CTA
 * final) e o lado da escola a consomem.
 */
type Slide = { kind: 'video'; id: string } | { kind: 'photo'; url: string }

interface School {
  /**
   * "school" é uma escola do sistema (packages, professores, matrícula,
   * agendamento pelo app); "creator_page" é uma página de criador do
   * BeWave Explore (Fase 3) — sem nada disso, mas com WhatsApp e redes
   * sociais. O componente é o mesmo porque o layout é o mesmo; só a copy
   * (e o cartão de contato) muda com o `kind`.
   */
  kind: 'school' | 'creator_page'
  name: string
  slug: string
  headline: string
  tagline: string | null
  about: string | null
  description: string | null
  phone: string | null
  avatar_url: string | null
  primary_color: string
  secondary_color: string
  city: string | null
  state: string | null
  address: string | null
  latitude: number | null
  longitude: number | null
  features: string[]
  custom_features?: CustomSchoolFeature[]
  show_whatsapp?: boolean
  trial_package?: TrialPackage | null
  registration_enabled: boolean
  /** Taxa de matrícula configurada pela escola. Sem isto a landing afirmava
      "sem taxa de matrícula" para todas, inclusive as que cobram. */
  requires_enrollment: boolean
  enrollment_price: number | null
  show_map: boolean
  gallery: string[]
  /**
   * IDs de vídeos do YouTube. OPCIONAL, e sempre lido com guarda: só a página
   * de criador manda (Explore::PagePresenter); a escola não manda nada, e uma
   * leitura sem `?? []` derrubaria a landing de TODAS as escolas.
   */
  videos?: string[]
  packages: Package[]
  teachers: Teacher[]
  events: EventItem[]
  operating_hours: OperatingHours | null
  /** Produtos e serviços cadastrados. Vazio para escola (só página de criador). */
  catalog: CatalogEntry[]
  host: Host | null
  /** Perguntas escritas pela IA. Vazio quando a página caiu no template. */
  faq: { question: string; answer: string }[]
  // Opcionais — populáveis pelo backend no futuro. Sem dado, cai em estados honestos.
  instagram_url?: string | null
  /** Rótulo do nicho (ex.: "Fotógrafo") — só páginas de criador preenchem. */
  category_label?: string | null
  /** Só páginas de criador preenchem — o cadastro de escola não tem esses campos. */
  social?: SocialLinks
  /** Só páginas de criador — vira um dos destaques em "Sobre". */
  years_experience?: number | null
}

interface Props {
  school: School
  /** Alimenta a busca e o filtro de cidade do header, iguais aos do diretório. */
  cities: CityOption[]
  registered: boolean
}

/* ─────────────────────────── Helpers ─────────────────────────── */

const formatPrice = (value: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
    value,
  )

const initials = (name: string) =>
  name
    .split(' ')
    .map((n) => n[0])
    .slice(0, 2)
    .join('')
    .toUpperCase()

const onlyDigits = (value: string) => value.replace(/\D/g, '')

// Máscara de CPF: 000.000.000-00
const formatCpf = (value: string) =>
  onlyDigits(value)
    .slice(0, 11)
    .replace(/^(\d{3})(\d)/, '$1.$2')
    .replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/^(\d{3})\.(\d{3})\.(\d{3})(\d)/, '$1.$2.$3-$4')

// Máscara de telefone BR: (85) 99999-9999 / (85) 9999-9999
const formatPhone = (value: string) => {
  const d = onlyDigits(value).slice(0, 11)
  if (d.length <= 10) {
    return d.replace(/^(\d{2})(\d)/, '($1) $2').replace(/(\d{4})(\d)/, '$1-$2')
  }
  return d.replace(/^(\d{2})(\d)/, '($1) $2').replace(/(\d{5})(\d)/, '$1-$2')
}

// Descrições curtas por recurso, no estilo "O que este lugar oferece" do Airbnb.
const FEATURE_DESCRIPTIONS: Record<string, string> = {
  level_beginner: 'Do zero à primeira onda, no seu ritmo',
  level_intermediate: 'Evolua a leitura de ondas e as manobras',
  level_advanced: 'Refino técnico para surfistas experientes',
  board_rental: 'Pranchas disponíveis para a sua aula',
  board_storage: 'Deixe sua prancha guardada na escola',
  shop: 'Acessórios e equipamentos à venda',
  wifi: 'Internet liberada no espaço',
  parking: 'Vagas para deixar o carro com tranquilidade',
  restroom: 'Banheiros à disposição dos alunos',
  shower: 'Enxágue depois de cair na água',
  changing_room: 'Espaço reservado para se trocar',
  lockers: 'Guarde seus pertences com segurança',
  snacks: 'Lanches e bebidas no local',
  accessibility: 'Estrutura pensada para acessibilidade',
  insurance: 'Aulas acompanhadas com segurança',
  photos: 'Registro das suas melhores ondas',
  kids: 'Turmas pensadas para as crianças',
  english: 'Professores que atendem em inglês',
  first_aid: 'Equipe preparada para primeiros socorros',
}

/* ─────────────────────────── Página ─────────────────────────── */

export default function SchoolLanding({ school, cities, registered }: Props) {
  const brand = school.primary_color || '#0ea5e9'
  const location = [school.city, school.state].filter(Boolean).join(', ')

  const [registerOpen, setRegisterOpen] = useState(false)
  const [successOpen, setSuccessOpen] = useState(registered)
  const [shareOpen, setShareOpen] = useState(false)
  const { favorited, toggle: toggleFavorite } = useFavorite(school.slug)

  const openRegister = () => setRegisterOpen(true)
  const openShare = () => setShareOpen(true)

  // Botão de contato via WhatsApp: só aparece se a escola tiver telefone E o
  // toggle "Botão WhatsApp" estiver ligado (default ligado para escolas antigas).
  // `whatsappLink` é o helper compartilhado (lib/publicText) — a lógica de DDI
  // estava duplicada aqui e divergiria na primeira mudança.
  const whatsappUrl =
    school.show_whatsapp !== false ? whatsappLink(school.phone) : null

  const minPrice = useMemo(() => {
    const prices = school.packages
      .map((p) => p.price)
      .filter((p): p is number => p != null)
    return prices.length ? Math.min(...prices) : null
  }, [school.packages])

  return (
    <div className="min-h-screen bg-bewave-surface text-gray-900 antialiased">
      <Head
        title={`${school.name} — ${school.kind === 'creator_page' ? (school.category_label ?? 'BeWave Explore') : 'Aulas de surf'}`}
      />

      <PublicHeader brandHref="/" cities={cities} />

      <div className={cn(PUBLIC_CONTAINER, 'mb-10 mt-4')}>
        <div className="pt-4">
          <Breadcrumb
            items={[
              { label: 'Início', href: '/' },
              { label: 'Explorar', href: EXPLORE_PATH },
              { label: school.name },
            ]}
          />
        </div>

        {/* HERO — galeria (mobile: ações flutuando sobre a foto) */}
        <section className="pt-3 md:pt-6">
          <Gallery
            school={school}
            brand={brand}
            favorited={favorited}
            onToggleFavorite={toggleFavorite}
            onShare={openShare}
          />
        </section>

        {/* Título (abaixo da galeria, ações à direita no desktop) */}
        <div className="pt-5 md:pt-6">
          <TitleBlock
            school={school}
            location={location}
            brand={brand}
            favorited={favorited}
            onToggleFavorite={toggleFavorite}
            onShare={openShare}
          />
        </div>

        {/* CORPO — 2 colunas com card flutuante */}
        <div className="grid grid-cols-1 gap-x-16 gap-y-10 pb-16 pt-8 lg:grid-cols-[1fr_minmax(340px,380px)]">
          <div className="min-w-0">
            <HostStrip school={school} location={location} brand={brand} />
            {school.trial_package && (
              <>
                <Divider />
                <TrialHighlight
                  trial={school.trial_package}
                  brand={brand}
                  onRegister={openRegister}
                  formatPrice={formatPrice}
                />
              </>
            )}
            <Divider />
            <AboutSection school={school} brand={brand} />
            <Divider />
            <OffersSection school={school} brand={brand} />
            {school.catalog.length > 0 && (
              <>
                <Divider />
                <CatalogSection
                  catalog={school.catalog}
                  brand={brand}
                  formatPrice={formatPrice}
                />
              </>
            )}
            {school.packages.length > 0 && (
              <>
                <Divider />
                <PackagesSection
                  school={school}
                  brand={brand}
                  onRegister={openRegister}
                  formatPrice={formatPrice}
                />
              </>
            )}
            {school.operating_hours && (
              <>
                <Divider />
                <OperatingHoursSection hours={school.operating_hours} />
              </>
            )}
            {school.host && (
              <>
                <Divider />
                <HostSection host={school.host} brand={brand} />
              </>
            )}
            {/* Do 2º vídeo em diante. O 1º não entra aqui: ele já está no
                destaque da vitrine, e repetir seria mostrar a mesma capa
                duas vezes na mesma tela. */}
            {(school.videos ?? []).length > 1 && (
              <>
                <Divider />
                <VideosSection
                  videos={(school.videos ?? []).slice(1)}
                  schoolName={school.name}
                />
              </>
            )}
            {school.faq.length > 0 && (
              <>
                <Divider />
                <FaqSection faq={school.faq} />
              </>
            )}
          </div>

          {/* Card flutuante */}
          <aside className="hidden lg:block">
            <div className="sticky top-24">
              <BookingCard
                school={school}
                brand={brand}
                minPrice={minPrice}
                whatsappUrl={whatsappUrl}
                onRegister={openRegister}
              />
            </div>
          </aside>
        </div>
      </div>

      {/* Faixas de largura total: fora do grid de 2 colunas de propósito — dentro
          dele elas ficariam presas à coluna da esquerda e não teriam como sangrar
          até as bordas. */}
      <Band>
        <LocationSection school={school} brand={brand} />
      </Band>

      {school.teachers.length > 0 && (
        <Band tone="white">
          <TeachersSection school={school} brand={brand} />
        </Band>
      )}

      {school.events.length > 0 && (
        <Band>
          <EventsSection school={school} brand={brand} />
        </Band>
      )}

      {/* CTA final full-bleed */}
      {school.registration_enabled && (
        <FinalCta school={school} brand={brand} onRegister={openRegister} />
      )}

      <Footer school={school} whatsappUrl={whatsappUrl} />

      {/* Barra fixa mobile */}
      <MobileBookingBar
        school={school}
        brand={brand}
        minPrice={minPrice}
        whatsappUrl={whatsappUrl}
        formatPrice={formatPrice}
        onRegister={openRegister}
      />

      {/* Dialogs */}
      <ShareDialog
        open={shareOpen}
        onOpenChange={setShareOpen}
        school={school}
      />
      {school.registration_enabled && (
        <RegisterDialog
          open={registerOpen}
          onOpenChange={setRegisterOpen}
          slug={school.slug}
          brand={brand}
          onSuccess={() => {
            setRegisterOpen(false)
            setSuccessOpen(true)
          }}
        />
      )}
      <SuccessDialog
        open={successOpen}
        onOpenChange={setSuccessOpen}
        brand={brand}
        schoolName={school.name}
      />
    </div>
  )
}

/* ─────────────────────────── Header ─────────────────────────── */

/* Ações de Compartilhar / Salvar — inline (desktop) ou flutuando (mobile). */
function PageActions({
  variant,
  favorited,
  onToggleFavorite,
  onShare,
}: {
  variant: 'inline' | 'floating'
  favorited: boolean
  onToggleFavorite: () => void
  onShare: () => void
}) {
  if (variant === 'floating') {
    return (
      <div className="flex items-center gap-2">
        <FloatingButton label="Compartilhar" onClick={onShare}>
          <Share aria-hidden="true" className="h-4 w-4" />
        </FloatingButton>
        <FloatingButton label="Salvar" onClick={onToggleFavorite}>
          <Heart
            aria-hidden="true"
            className={cn(
              'h-4 w-4',
              favorited && 'fill-rose-500 text-rose-500',
            )}
          />
        </FloatingButton>
      </div>
    )
  }

  return (
    <div className="flex items-center gap-1">
      <button
        type="button"
        onClick={onShare}
        className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-gray-800 transition-colors hover:bg-gray-100"
      >
        <Share aria-hidden="true" className="h-4 w-4" />
        <span className="underline underline-offset-2">Compartilhar</span>
      </button>
      <button
        type="button"
        onClick={onToggleFavorite}
        className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-gray-800 transition-colors hover:bg-gray-100"
      >
        <Heart
          aria-hidden="true"
          className={cn('h-4 w-4', favorited && 'fill-rose-500 text-rose-500')}
        />
        <span className="underline underline-offset-2">
          {favorited ? 'Salvo' : 'Salvar'}
        </span>
      </button>
    </div>
  )
}

function FloatingButton({
  children,
  onClick,
  label,
}: {
  children: React.ReactNode
  onClick: () => void
  label: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className="flex h-9 w-9 items-center justify-center rounded-full bg-white text-gray-800 shadow-md ring-1 ring-black/5 transition-transform hover:scale-105 active:scale-95"
    >
      {children}
    </button>
  )
}

/* ─────────────────────────── Modal de compartilhar ─────────────────────────── */

function ShareDialog({
  open,
  onOpenChange,
  school,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  school: School
}) {
  const [copied, setCopied] = useState(false)
  const url = typeof window !== 'undefined' ? window.location.href : ''
  const summary = [school.city, school.state].filter(Boolean).join(', ')
  const text = `${school.name} — aulas de surf`
  const cover = school.gallery[0] || school.avatar_url

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      /* clipboard indisponível */
    }
  }

  const nativeShare = async () => {
    try {
      if (navigator.share)
        await navigator.share({ title: school.name, text, url })
    } catch {
      /* usuário cancelou */
    }
  }

  const enc = encodeURIComponent

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-left text-xl">
            Compartilhe esta escola
          </DialogTitle>
          <DialogDescription className="sr-only">
            Opções de compartilhamento
          </DialogDescription>
        </DialogHeader>

        {/* Resumo */}
        <div className="mt-1 flex items-center gap-3 border-b border-gray-100 pb-4">
          {cover ? (
            <img
              src={cover}
              alt=""
              className="h-14 w-14 rounded-xl object-cover"
            />
          ) : (
            <span className="flex h-14 w-14 items-center justify-center rounded-xl bg-gray-100 text-sm font-bold text-gray-500">
              {initials(school.name)}
            </span>
          )}
          <p className="text-sm text-gray-700">
            <span className="font-semibold text-gray-900">{school.name}</span>
            {summary && <> · {summary}</>}
          </p>
        </div>

        {/* Opções */}
        <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <ShareOption
            icon={Link2}
            label={copied ? 'Link copiado!' : 'Copiar link'}
            onClick={copyLink}
          />
          <ShareOption
            icon={MessageCircle}
            label="WhatsApp"
            href={`https://wa.me/?text=${enc(`${text} ${url}`)}`}
          />
          <ShareOption
            icon={Mail}
            label="Email"
            href={`mailto:?subject=${enc(text)}&body=${enc(url)}`}
          />
          <ShareOption
            icon={Facebook}
            label="Facebook"
            href={`https://www.facebook.com/sharer/sharer.php?u=${enc(url)}`}
          />
          <ShareOption
            icon={Twitter}
            label="X (Twitter)"
            href={`https://twitter.com/intent/tweet?text=${enc(text)}&url=${enc(url)}`}
          />
          {typeof navigator !== 'undefined' && 'share' in navigator && (
            <ShareOption
              icon={MoreHorizontal}
              label="Mais opções"
              onClick={nativeShare}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

function ShareOption({
  icon: Icon,
  label,
  href,
  onClick,
}: {
  icon: typeof Link2
  label: string
  href?: string
  onClick?: () => void
}) {
  const className =
    'flex items-center gap-3 rounded-xl border border-gray-200 px-4 py-3.5 text-sm font-medium text-gray-900 transition-colors hover:border-gray-900'
  const content = (
    <>
      <Icon className="h-5 w-5 text-gray-700" />
      {label}
    </>
  )
  if (href) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={className}
      >
        {content}
      </a>
    )
  }
  return (
    <button type="button" onClick={onClick} className={className}>
      {content}
    </button>
  )
}

/* ─────────────────────────── Título ─────────────────────────── */

function TitleBlock({
  school,
  location,
  brand,
  favorited,
  onToggleFavorite,
  onShare,
}: {
  school: School
  location: string
  brand: string
  favorited: boolean
  onToggleFavorite: () => void
  onShare: () => void
}) {
  return (
    <div>
      <div className="flex items-start justify-between gap-4">
        <h1 className="text-[26px] font-semibold leading-tight tracking-tight text-gray-900 md:text-[32px]">
          {school.headline}
        </h1>
        {/* Ações no desktop; no mobile ficam flutuando sobre a foto */}
        <div className="hidden shrink-0 md:block">
          <PageActions
            variant="inline"
            favorited={favorited}
            onToggleFavorite={onToggleFavorite}
            onShare={onShare}
          />
        </div>
      </div>
      {school.tagline && (
        <p className="mt-1.5 text-lg text-gray-500">{school.tagline}</p>
      )}
      <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1.5 text-sm text-gray-700">
        <span className="inline-flex items-center gap-1 font-semibold">
          <Star className="h-4 w-4 fill-current" style={{ color: brand }} />
          {PLACEHOLDER_RATING}
        </span>
        {location && (
          <>
            <span className="text-gray-300">·</span>
            <span className="font-medium">{location}</span>
          </>
        )}
      </div>
    </div>
  )
}

/* ─────────────────────────── Galeria (hero) ─────────────────────────── */

function Gallery({
  school,
  brand,
  favorited,
  onToggleFavorite,
  onShare,
}: {
  school: School
  brand: string
  favorited: boolean
  onToggleFavorite: () => void
  onShare: () => void
}) {
  // A vitrine é fotos MAIS o vídeo em destaque, e o union é local: `gallery`
  // continua sendo `string[]` para o resto da landing (compartilhamento,
  // og:image, CTA final) e para o lado da escola, que não manda vídeo nenhum.
  //
  // O primeiro vídeo vai na FRENTE: é o que "fica em destaque, junto com as
  // fotos". Sem vídeo, `slides` é exatamente a galeria de antes.
  const destaque = (school.videos ?? [])[0]
  const images: Slide[] = [
    ...(destaque ? [{ kind: 'video' as const, id: destaque }] : []),
    ...school.gallery.map((url) => ({ kind: 'photo' as const, url })),
  ]
  const [index, setIndex] = useState(0)
  const [tocando, setTocando] = useState<string | null>(null)
  const swipeStart = useRef<number | null>(null)
  // Os dois layouts abaixo (mosaico e carrossel) coexistem no DOM — o `md:` só
  // esconde um COM CSS, não desmonta. Como ambos liam o mesmo `tocando`, um
  // play montava DOIS iframes do YouTube com autoplay: dois áudios sobrepostos,
  // e pausar o visível não calava o escondido. Este flag garante que só o
  // layout de fato visível receba `playing`.
  const isMobile = useIsMobile()

  if (images.length === 0) {
    return (
      <div className="flex aspect-[16/10] items-center justify-center rounded-3xl border border-gray-200 bg-white md:aspect-[5/2]">
        {school.avatar_url ? (
          <img
            src={school.avatar_url}
            alt={school.name}
            className="h-24 w-24 rounded-2xl object-cover"
          />
        ) : (
          <span className="text-5xl font-bold" style={{ color: brand }}>
            {initials(school.name)}
          </span>
        )}
      </div>
    )
  }

  const step = (delta: number) => {
    // Sem isto, o áudio do vídeo continua tocando por baixo da foto seguinte.
    setTocando(null)
    setIndex((current) => (current + delta + images.length) % images.length)
  }

  return (
    <div className="relative">
      {/* Desktop: mosaico, que mostra várias fotos de uma vez sem abrir nada.
          Mobile: uma foto por vez com setas, porque o mosaico não cabe. */}
      <div className="hidden md:block">
        <DesktopGrid
          slides={images}
          schoolName={school.name}
          playing={isMobile ? null : tocando}
          onPlay={setTocando}
        />
      </div>

      <div
        className="relative aspect-[16/10] w-full touch-pan-y overflow-hidden rounded-3xl bg-gray-100 md:hidden"
        onPointerDown={(event) => {
          if (event.pointerType === 'mouse') return
          swipeStart.current = event.clientX
        }}
        onPointerUp={(event) => {
          if (swipeStart.current === null) return
          const delta = event.clientX - swipeStart.current
          swipeStart.current = null
          if (Math.abs(delta) >= 40) step(delta < 0 ? 1 : -1)
        }}
        onPointerCancel={() => {
          swipeStart.current = null
        }}
      >
        <div
          className="flex h-full transition-transform duration-300 ease-out"
          style={{ transform: `translateX(-${index * 100}%)` }}
        >
          {images.map((slide, position) =>
            slide.kind === 'video' ? (
              <div key={`video-${slide.id}`} className="h-full w-full shrink-0">
                <VideoDoCriador
                  id={slide.id}
                  title={`${school.name} — vídeo`}
                  playing={isMobile && tocando === slide.id}
                  onPlay={() => setTocando(slide.id)}
                />
              </div>
            ) : (
              <img
                key={slide.url}
                src={slide.url}
                alt={
                  images.length > 1
                    ? `${school.name} — foto ${position + 1}`
                    : school.name
                }
                loading={position === 0 ? 'eager' : 'lazy'}
                className="h-full w-full shrink-0 object-cover"
              />
            ),
          )}
        </div>

        {/* Setas sempre visíveis, não só no hover: é o que sinaliza que existem
          mais fotos. Antes isso era papel do botão "Ver todas as fotos", que
          abria um dialog. */}
        {images.length > 1 && (
          <>
            <button
              type="button"
              aria-label="Foto anterior"
              title="Foto anterior"
              onClick={() => step(-1)}
              className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full bg-white/90 p-2 shadow-md transition-transform hover:scale-105"
            >
              <ChevronLeft aria-hidden="true" className="h-5 w-5" />
            </button>
            <button
              type="button"
              aria-label="Próxima foto"
              title="Próxima foto"
              onClick={() => step(1)}
              className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-white/90 p-2 shadow-md transition-transform hover:scale-105"
            >
              <ChevronRight aria-hidden="true" className="h-5 w-5" />
            </button>

            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-x-0 bottom-4 flex justify-center gap-1.5"
            >
              {images.map((slide, position) => (
                <span
                  key={slide.kind === 'video' ? `video-${slide.id}` : slide.url}
                  className={cn(
                    'h-1.5 w-1.5 rounded-full transition-colors',
                    position === index ? 'bg-white' : 'bg-white/50',
                  )}
                />
              ))}
            </div>
            <span aria-live="polite" className="sr-only">
              {index + 1} de {images.length}
            </span>

            <span className="absolute bottom-4 right-4 rounded-full bg-black/60 px-2.5 py-1 text-xs font-medium text-white">
              {index + 1} / {images.length}
            </span>
          </>
        )}
      </div>

      {/* Ações flutuando sobre a foto (mobile) */}
      <div className="absolute right-3 top-3 md:hidden">
        <PageActions
          variant="floating"
          favorited={favorited}
          onToggleFavorite={onToggleFavorite}
          onShare={onShare}
        />
      </div>
    </div>
  )
}

/**
 * Mosaico do desktop: uma foto grande e até quatro menores, todas visíveis ao
 * mesmo tempo. Não abre nada ao clicar — a navegação por dialog foi removida, e
 * aqui ela não faz falta porque as fotos já estão à vista.
 */
function DesktopGrid({
  slides,
  schoolName,
  playing,
  onPlay,
}: {
  slides: Slide[]
  schoolName: string
  playing: string | null
  onPlay: (id: string) => void
}) {
  // O primeiro slide manda no slot grande — é ali que o vídeo em destaque
  // aparece, do tamanho de duas fotos.
  const tile = (slide: Slide, alt: string, eager: boolean) =>
    slide.kind === 'video' ? (
      <VideoDoCriador
        id={slide.id}
        title={alt}
        playing={playing === slide.id}
        onPlay={() => onPlay(slide.id)}
      />
    ) : (
      <img
        src={slide.url}
        alt={alt}
        loading={eager ? undefined : 'lazy'}
        className="h-full w-full object-cover"
      />
    )

  if (slides.length === 1) {
    return (
      <div className="aspect-[5/2] w-full overflow-hidden rounded-3xl">
        {tile(slides[0], schoolName, true)}
      </div>
    )
  }

  const [big, ...rest] = slides
  const small = rest.slice(0, 4)

  return (
    <div className="grid h-[440px] grid-cols-4 grid-rows-2 gap-2 overflow-hidden rounded-3xl bg-gray-100">
      <div className="col-span-2 row-span-2 h-full w-full overflow-hidden">
        {tile(big, `${schoolName} — 1`, true)}
      </div>
      {small.map((slide, index) => (
        <div
          key={slide.kind === 'video' ? `video-${slide.id}` : slide.url}
          className="h-full w-full overflow-hidden"
        >
          {tile(slide, `${schoolName} — ${index + 2}`, false)}
        </div>
      ))}
    </div>
  )
}

/* ─────────────────────────── Host strip ─────────────────────────── */

function HostStrip({
  school,
  location,
  brand,
}: {
  school: School
  location: string
  brand: string
}) {
  return (
    <div className="flex items-start justify-between gap-4 pb-2">
      <div>
        <h2 className="text-xl font-semibold text-gray-900 md:text-[22px]">
          {school.kind === 'creator_page'
            ? (school.category_label ?? 'Negócio')
            : 'Escola de surf'}
          {location ? ` em ${location}` : ''}
        </h2>
        <p className="mt-1 flex flex-wrap items-center gap-x-1.5 text-sm text-gray-500">
          {school.teachers.length > 0 && (
            <span>
              {school.teachers.length}{' '}
              {school.teachers.length === 1 ? 'professor' : 'professores'}
            </span>
          )}
          {school.teachers.length > 0 && school.packages.length > 0 && (
            <span>·</span>
          )}
          {school.packages.length > 0 && (
            <span>
              {school.packages.length}{' '}
              {school.packages.length === 1 ? 'pacote' : 'pacotes'}
            </span>
          )}
        </p>
      </div>
      {school.avatar_url ? (
        <img
          src={school.avatar_url}
          alt={school.name}
          className="h-12 w-12 shrink-0 rounded-full object-cover ring-1 ring-gray-100"
        />
      ) : (
        <span
          className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white"
          style={{ backgroundColor: brand }}
        >
          {initials(school.name)}
        </span>
      )}
    </div>
  )
}

/* ─────────────────────────── Sobre ─────────────────────────── */

/* ─────────────────────── Aula experimental (destaque) ─────────────────────── */

/**
 * Os outros vídeos do criador, fora da vitrine.
 *
 * O mosaico do topo só mostra cinco quadros: enfiar o 2º e o 3º vídeo lá
 * empurraria fotos para fora. Aqui eles têm espaço próprio — e a mesma capa
 * estática do destaque, sem iframe até o clique.
 */
function VideosSection({
  videos,
  schoolName,
}: {
  videos: string[]
  schoolName: string
}) {
  const [tocando, setTocando] = useState<string | null>(null)

  return (
    <section>
      <h2 className="text-xl font-bold text-gray-900">Vídeos</h2>
      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
        {videos.map((id, index) => (
          <div
            key={id}
            className="aspect-video overflow-hidden rounded-2xl bg-gray-100"
          >
            <VideoDoCriador
              id={id}
              title={`${schoolName} — vídeo ${index + 2}`}
              playing={tocando === id}
              onPlay={() => setTocando(id)}
            />
          </div>
        ))}
      </div>
    </section>
  )
}

function TrialHighlight({
  trial,
  brand,
  onRegister,
  formatPrice,
}: {
  trial: TrialPackage
  brand: string
  onRegister: () => void
  formatPrice: (v: number) => string
}) {
  const priceLabel = trial.free
    ? 'Gratuita'
    : trial.price != null
      ? formatPrice(trial.price)
      : 'Valor sob consulta'

  return (
    <section id="aula-experimental" className="scroll-mt-24 py-8">
      <div
        className="overflow-hidden rounded-2xl border p-6 sm:p-8"
        style={{
          borderColor: `${brand}33`,
          // O tom da marca a 5% precisa de uma base opaca: sobre o cinza da página
          // ele compunha para quase o próprio cinza e o destaque virava um afundado.
          background: `linear-gradient(${brand}0d, ${brand}0d), #fff`,
        }}
      >
        <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <span
              className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold"
              style={{ background: `${brand}1a`, color: brand }}
            >
              <Sparkles className="h-3.5 w-3.5" />
              Aula experimental
            </span>
            <h2 className="mt-3 text-xl font-semibold text-gray-900 sm:text-2xl">
              {trial.name}
            </h2>
            {trial.description && (
              <p className="mt-1.5 max-w-xl text-[15px] leading-relaxed text-gray-600">
                {trial.description}
              </p>
            )}
            <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-600">
              <span className="inline-flex items-center gap-1.5">
                <Ticket className="h-4 w-4" style={{ color: brand }} />
                {trial.lessons_count}{' '}
                {trial.lessons_count === 1 ? 'aula' : 'aulas'}
              </span>
              <span className="text-lg font-semibold text-gray-900">
                {priceLabel}
              </span>
            </div>
          </div>
          <div className="shrink-0">
            {/* TODO Fase 3: abrir fluxo de agendamento (calendário) direto daqui. */}
            <button
              type="button"
              onClick={onRegister}
              className="inline-flex w-full items-center justify-center gap-2 rounded-xl px-6 py-3 text-sm font-semibold text-white transition-opacity hover:opacity-90 sm:w-auto"
              style={{ background: brand }}
            >
              Quero experimentar
              <ArrowRight aria-hidden="true" className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </section>
  )
}

function AboutSection({ school, brand }: { school: School; brand: string }) {
  const about = school.about || school.description
  const stats = buildStats(school)

  return (
    <SectionShell
      id="sobre"
      title={school.kind === 'creator_page' ? 'Sobre' : 'Sobre a escola'}
    >
      {about && (
        <p className="max-w-2xl whitespace-pre-line text-[15px] leading-relaxed text-gray-700">
          {about}
        </p>
      )}
      {stats.length > 0 && (
        <div className="mt-7 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {stats.map((s) => (
            <div
              key={s.label}
              className="flex flex-col gap-3 rounded-2xl p-4"
              style={{ backgroundColor: `${brand}0d` }}
            >
              <s.icon
                className="h-5 w-5"
                style={{ color: brand }}
                strokeWidth={1.75}
              />
              <div>
                <p className="text-sm font-semibold text-gray-900">{s.value}</p>
                <p className="text-xs text-gray-500">{s.label}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </SectionShell>
  )
}

// Estatísticas derivadas apenas de dados reais — sem números fabricados.
function buildStats(school: School) {
  const stats: { icon: typeof Award; value: string; label: string }[] = []

  if (school.tagline) {
    stats.push({ icon: Award, value: school.tagline, label: 'Trajetória' })
  }
  const levels =
    school.features.filter((f) => f.startsWith('level_')).length +
    (school.custom_features || []).filter((f) => f.group === 'level').length
  if (levels > 0) {
    stats.push({
      icon: Waves,
      value: 'Todos os níveis',
      label: `${levels} tipos de aula`,
    })
  }
  if (school.teachers.length > 0) {
    stats.push({
      icon: Users,
      value: `${school.teachers.length} ${school.teachers.length === 1 ? 'professor' : 'professores'}`,
      label: 'Equipe da escola',
    })
  }

  if (school.kind === 'creator_page') {
    // Sem app, sem agendamento: o destaque possível é a experiência
    // declarada no cadastro — quando não há, não inventa um quarto stat.
    if (school.years_experience) {
      stats.push({
        icon: CalendarDays,
        value: `${school.years_experience} ${school.years_experience === 1 ? 'ano' : 'anos'}`,
        label: 'De experiência',
      })
    }
  } else {
    stats.push({
      icon: CalendarDays,
      value: 'Agende pelo app',
      label: 'Reserve suas aulas',
    })
  }

  return stats.slice(0, 4)
}

/* ─────────────────────── O que a escola oferece ─────────────────────── */

interface OfferEntry {
  key: string
  label: string
  description?: string
  Icon: LucideIcon
}

// Unifica selos do catálogo fixo + selos criados pela escola em uma lista só.
function collectOffers(school: School): OfferEntry[] {
  const predefined: OfferEntry[] = (school.features || [])
    .filter((key) => SCHOOL_FEATURE_MAP[key])
    .map((key) => ({
      key,
      label: SCHOOL_FEATURE_MAP[key].label,
      description: FEATURE_DESCRIPTIONS[key],
      Icon: SCHOOL_FEATURE_MAP[key].icon,
    }))

  const custom: OfferEntry[] = (school.custom_features || [])
    .filter((f) => f.label?.trim())
    .map((f) => ({
      key: f.id,
      label: f.label,
      Icon: resolveFeatureIcon(f.icon),
    }))

  // Selos criados pela escola vêm primeiro: são os diferenciais dela e não
  // devem ficar escondidos atrás do "Mostrar todos" quando há muitos fixos.
  return [...custom, ...predefined]
}

function OffersSection({ school, brand }: { school: School; brand: string }) {
  const offers = collectOffers(school)
  const [showAll, setShowAll] = useState(false)

  if (offers.length === 0) return null

  const visible = offers.slice(0, 8)

  return (
    <SectionShell title="O que essa escola oferece">
      <div className="grid grid-cols-1 gap-x-8 gap-y-5 sm:grid-cols-2">
        {visible.map((offer) => (
          <OfferItem key={offer.key} offer={offer} brand={brand} />
        ))}
      </div>
      {offers.length > 8 && (
        <>
          <button
            type="button"
            onClick={() => setShowAll(true)}
            className="mt-8 inline-flex items-center gap-2 rounded-xl border border-gray-800 bg-white px-5 py-3 text-sm font-semibold text-gray-900 transition-colors hover:bg-gray-50"
          >
            Mostrar todos os {offers.length} recursos
          </button>
          <AllOffersDialog
            open={showAll}
            onOpenChange={setShowAll}
            offers={offers}
            brand={brand}
          />
        </>
      )}
    </SectionShell>
  )
}

function OfferItem({ offer, brand }: { offer: OfferEntry; brand: string }) {
  const Icon = offer.Icon

  return (
    <div className="flex items-start gap-4 border-b border-gray-200 pb-5">
      <Icon
        className="mt-0.5 h-6 w-6 shrink-0"
        style={{ color: brand }}
        strokeWidth={1.75}
      />
      <div>
        <p className="text-[15px] font-medium text-gray-900">{offer.label}</p>
        {offer.description && (
          <p className="mt-0.5 text-sm text-gray-500">{offer.description}</p>
        )}
      </div>
    </div>
  )
}

function AllOffersDialog({
  open,
  onOpenChange,
  offers,
  brand,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  offers: OfferEntry[]
  brand: string
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>O que essa escola oferece</DialogTitle>
          <DialogDescription>
            Tudo que você encontra na estrutura da escola.
          </DialogDescription>
        </DialogHeader>
        <div className="mt-2 space-y-1">
          {offers.map((offer) => (
            <OfferItem key={offer.key} offer={offer} brand={brand} />
          ))}
        </div>
      </DialogContent>
    </Dialog>
  )
}

/* ─────────────────────── Galeria (seção secundária) ─────────────────────── */

/* ─────────────────────────── Pacotes ─────────────────────────── */

function PackagesSection({
  school,
  brand,
  onRegister,
  formatPrice,
}: {
  school: School
  brand: string
  onRegister: () => void
  formatPrice: (v: number) => string
}) {
  return (
    <SectionShell id="pacotes" title="Aulas e pacotes">
      <Rail ariaLabel="Pacotes de aula">
        {school.packages.map((pkg) => (
          <div
            key={pkg.id}
            role="listitem"
            className="flex w-40 shrink-0 snap-start flex-col rounded-2xl p-4 sm:w-44"
            style={{ backgroundColor: `${brand}0d` }}
          >
            <h3 className="line-clamp-2 text-sm font-semibold text-gray-900">
              {pkg.name}
            </h3>
            <p className="mt-1 text-xs text-gray-500">
              {pkg.lessons_count} {pkg.lessons_count === 1 ? 'aula' : 'aulas'}
              {pkg.validity_days ? ` · ${pkg.validity_days} dias` : ''}
            </p>

            {/* mt-auto ancora a linha do preço na base do card: com nomes de
                1 e 2 linhas, os valores ficam todos alinhados entre os cards
                (que se esticam à mesma altura dentro do Rail). */}
            <div className="mt-auto flex items-end justify-between gap-2 pt-4">
              {pkg.price != null ? (
                <p className="text-lg font-bold text-gray-900">
                  {formatPrice(pkg.price)}
                </p>
              ) : (
                <p className="text-xs font-medium text-gray-500">
                  Sob consulta
                </p>
              )}
              {school.registration_enabled && (
                <button
                  type="button"
                  onClick={onRegister}
                  aria-label={`Começar com o pacote ${pkg.name}`}
                  className="flex size-8 shrink-0 items-center justify-center rounded-full text-white transition-opacity hover:opacity-90"
                  style={{ backgroundColor: brand }}
                >
                  <ArrowRight aria-hidden="true" className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        ))}
      </Rail>
    </SectionShell>
  )
}

/* ─────────────────────────── Catálogo ─────────────────────────── */

/**
 * Os produtos e serviços do negócio, com foto e preço.
 *
 * Separado em duas listas ("O que oferecemos" e "O que vendemos") porque são
 * duas decisões diferentes para quem lê: contratar um atendimento e comprar uma
 * coisa. Misturados numa fileira só, uma aula de surf e uma prancha usada
 * apareciam como itens do mesmo tipo.
 *
 * Preço nulo vira "Sob consulta", nunca R$ 0,00 — o que o criador não informou
 * não pode virar promessa de grátis.
 */
function CatalogSection({
  catalog,
  brand,
  formatPrice,
}: {
  catalog: CatalogEntry[]
  brand: string
  formatPrice: (v: number) => string
}) {
  const servicos = catalog.filter((item) => item.kind === 'servico')
  const produtos = catalog.filter((item) => item.kind === 'produto')

  return (
    <div id="catalogo" className="scroll-mt-24 space-y-8 py-8">
      {servicos.length > 0 && (
        <CatalogGroup
          title="O que oferecemos"
          items={servicos}
          brand={brand}
          formatPrice={formatPrice}
        />
      )}
      {produtos.length > 0 && (
        <CatalogGroup
          title="O que vendemos"
          items={produtos}
          brand={brand}
          formatPrice={formatPrice}
        />
      )}
    </div>
  )
}

function CatalogGroup({
  title,
  items,
  brand,
  formatPrice,
}: {
  title: string
  items: CatalogEntry[]
  brand: string
  formatPrice: (v: number) => string
}) {
  return (
    <section>
      <h2 className="text-xl font-semibold text-gray-900 md:text-[22px]">
        {title}
      </h2>
      <Rail ariaLabel={title}>
        {items.map((item) => (
          <article
            key={item.id}
            role="listitem"
            className="flex w-56 shrink-0 snap-start flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white"
          >
            {item.photos[0] ? (
              <img
                src={item.photos[0]}
                alt={item.name}
                loading="lazy"
                className="h-32 w-full object-cover"
              />
            ) : (
              <div
                aria-hidden="true"
                className="h-32 w-full"
                style={{ backgroundColor: `${brand}14` }}
              />
            )}

            <div className="flex flex-1 flex-col p-3">
              <h3 className="line-clamp-2 text-sm font-semibold text-gray-900">
                {item.name}
              </h3>
              {item.description && (
                <p className="mt-1 line-clamp-3 text-xs text-gray-500">
                  {item.description}
                </p>
              )}

              {/* mt-auto ancora o preço na base: com descrições de alturas
                  diferentes, os valores ficam alinhados entre os cards. */}
              <p className="mt-auto pt-3">
                {item.price != null ? (
                  <>
                    <span className="text-lg font-bold text-gray-900">
                      {formatPrice(item.price)}
                    </span>
                    {item.unit && (
                      <span className="text-xs font-medium text-gray-500">
                        {' '}
                        {item.unit}
                      </span>
                    )}
                  </>
                ) : (
                  <span className="text-xs font-medium text-gray-500">
                    Sob consulta
                  </span>
                )}
              </p>
            </div>
          </article>
        ))}
      </Rail>
    </section>
  )
}

/* ─────────────────────────── Horários ─────────────────────────── */

const DAY_NAMES = [
  'Domingo',
  'Segunda',
  'Terça',
  'Quarta',
  'Quinta',
  'Sexta',
  'Sábado',
]

/**
 * Dias e faixas de atendimento.
 *
 * Vale tanto para escola quanto para página de criador: as duas gravam o mesmo
 * shape. Curiosidade que justifica a seção existir — a escola já enviava
 * `operating_hours` nas props desde sempre, e NINGUÉM renderizava: o campo
 * existia só como interface TypeScript, então quem preenchia horário no admin
 * preenchia para nada.
 */
function OperatingHoursSection({ hours }: { hours: OperatingHours }) {
  const dias = (hours.days ?? []).map(Number).filter((d) => d >= 0 && d <= 6)
  const faixas = [hours.morning, hours.afternoon].filter(
    (f): f is { start?: string; end?: string } => Boolean(f?.start && f?.end),
  )

  if (dias.length === 0 && faixas.length === 0) return null

  return (
    <section id="horarios" className="scroll-mt-24 py-8">
      <h2 className="text-xl font-semibold text-gray-900 md:text-[22px]">
        Horário de atendimento
      </h2>

      {dias.length > 0 && (
        <ul className="mt-4 flex flex-wrap gap-2">
          {DAY_NAMES.map((nome, index) => {
            const atende = dias.includes(index)
            return (
              <li
                key={nome}
                className={cn(
                  'rounded-full px-3 py-1.5 text-sm font-medium',
                  atende
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-400 line-through',
                )}
              >
                {nome}
              </li>
            )
          })}
        </ul>
      )}

      {faixas.length > 0 && (
        <p className="mt-4 text-sm text-gray-600">
          {faixas.map((f) => `${f.start} às ${f.end}`).join(' · ')}
        </p>
      )}
    </section>
  )
}

/* ─────────────────────────── Quem está por trás ─────────────────────────── */

function HostSection({ host, brand }: { host: Host; brand: string }) {
  return (
    <section id="quem-atende" className="scroll-mt-24 py-8">
      <h2 className="text-xl font-semibold text-gray-900 md:text-[22px]">
        {host.kind === 'empresa' ? 'Sobre a empresa' : 'Quem vai te atender'}
      </h2>

      <div className="mt-4 flex items-start gap-4">
        {host.avatar_url ? (
          <img
            src={host.avatar_url}
            alt={host.name}
            loading="lazy"
            className="size-16 shrink-0 rounded-full object-cover ring-1 ring-gray-100"
          />
        ) : (
          <span
            aria-hidden="true"
            className="flex size-16 shrink-0 items-center justify-center rounded-full text-lg font-bold text-white"
            style={{ backgroundColor: brand }}
          >
            {initials(host.name)}
          </span>
        )}

        <div className="min-w-0">
          <p className="text-base font-semibold text-gray-900">{host.name}</p>
          {host.headline && (
            <p className="text-sm text-gray-500">{host.headline}</p>
          )}
          {host.bio && (
            <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-gray-700">
              {host.bio}
            </p>
          )}
        </div>
      </div>
    </section>
  )
}

/* ─────────────────────────── Perguntas frequentes ─────────────────────────── */

/**
 * As perguntas que a IA escreveu a partir do que o negócio informou.
 *
 * `<details>` nativo em vez de um acordeão de componente: é o mesmo
 * comportamento, funciona sem JavaScript e o texto fica no HTML servido — que
 * é o que interessa para a busca, já que FAQ é justamente o conteúdo que traz
 * gente de cauda longa.
 */
function FaqSection({ faq }: { faq: { question: string; answer: string }[] }) {
  return (
    <section id="perguntas" className="scroll-mt-24 py-8">
      <h2 className="text-xl font-semibold text-gray-900 md:text-[22px]">
        Perguntas frequentes
      </h2>

      <div className="mt-4 divide-y divide-gray-100 border-y border-gray-100">
        {faq.map((item) => (
          <details key={item.question} className="group py-3">
            <summary className="flex cursor-pointer list-none items-center justify-between gap-3 text-sm font-semibold text-gray-900 [&::-webkit-details-marker]:hidden">
              {item.question}
              <ChevronDown
                aria-hidden="true"
                className="size-4 shrink-0 text-gray-400 transition-transform duration-200 group-open:rotate-180 motion-reduce:transition-none"
              />
            </summary>
            <p className="mt-2 text-sm leading-relaxed text-gray-600">
              {item.answer}
            </p>
          </details>
        ))}
      </div>
    </section>
  )
}

/* ─────────────────────────── Professores ─────────────────────────── */

function TeachersSection({ school, brand }: { school: School; brand: string }) {
  return (
    <SectionShell title="Conheça os professores">
      <Rail ariaLabel="Professores">
        {school.teachers.map((teacher) => (
          <div
            key={teacher.id}
            role="listitem"
            className="flex w-24 shrink-0 snap-start flex-col items-center gap-2 text-center sm:w-28"
          >
            {teacher.avatar_url ? (
              <img
                src={teacher.avatar_url}
                alt={teacher.name}
                className="h-16 w-16 shrink-0 rounded-full object-cover"
              />
            ) : (
              <span
                className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full text-lg font-semibold text-white"
                style={{ backgroundColor: brand }}
              >
                {initials(teacher.name)}
              </span>
            )}
            {/* `w-full`: sem ele, este filho de um flex-col com `items-center`
                encolhe para o próprio conteúdo em vez de herdar a largura fixa
                do card — e um nome comprido, sem largura para truncar contra,
                vazava por cima do próximo cartão do trilho. */}
            <div className="w-full min-w-0">
              <p className="truncate text-sm font-semibold text-gray-900">
                {teacher.name}
              </p>
              <p className="truncate text-xs text-gray-500">
                {teacher.specialty || 'Professor de surf'}
                {teacher.years_experience
                  ? ` · ${teacher.years_experience}a`
                  : ''}
              </p>
            </div>
          </div>
        ))}
      </Rail>
    </SectionShell>
  )
}

/* ─────────────────────────── Eventos ─────────────────────────── */

function EventsSection({ school, brand }: { school: School; brand: string }) {
  return (
    <SectionShell id="eventos" title="Próximos eventos">
      <div className="grid gap-5 sm:grid-cols-2">
        {school.events.map((event) => (
          <div
            key={event.id}
            className="flex flex-col overflow-hidden rounded-3xl border border-gray-200 bg-white transition-shadow hover:shadow-[0_6px_24px_rgba(0,0,0,0.06)]"
          >
            {event.banner_url ? (
              <img
                src={event.banner_url}
                alt={event.title}
                className="aspect-[16/9] w-full object-cover"
              />
            ) : (
              <div
                className="flex aspect-[16/9] w-full items-center justify-center"
                style={{ backgroundColor: `${brand}12` }}
              >
                <Ticket className="h-8 w-8" style={{ color: brand }} />
              </div>
            )}
            <div className="flex flex-1 flex-col p-5">
              <span
                className="mb-2 w-fit rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide"
                style={{ backgroundColor: `${brand}14`, color: brand }}
              >
                {event.type}
              </span>
              <h3 className="text-base font-semibold text-gray-900">
                {event.title}
              </h3>
              <div className="mt-2 space-y-1 text-sm text-gray-500">
                <p className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" style={{ color: brand }} />{' '}
                  {event.date}
                </p>
                {event.location && (
                  <p className="flex items-center gap-2">
                    <MapPin className="h-4 w-4" style={{ color: brand }} />{' '}
                    {event.location}
                  </p>
                )}
              </div>
              <p className="mt-4 text-sm font-semibold text-gray-900">
                {event.price != null ? formatPrice(event.price) : 'Gratuito'}
              </p>
            </div>
          </div>
        ))}
      </div>
    </SectionShell>
  )
}

/* ─────────────────────────── Avaliações ─────────────────────────── */

/* ─────────────────────────── Localização ─────────────────────────── */

function LocationSection({ school, brand }: { school: School; brand: string }) {
  if (!school.show_map && !school.address) return null

  const hasCoords =
    school.show_map && school.latitude != null && school.longitude != null

  return (
    <SectionShell
      id="localizacao"
      title={
        school.kind === 'creator_page'
          ? 'Onde encontrar'
          : 'Onde você vai surfar'
      }
    >
      {school.address && (
        <p className="mb-5 flex items-start gap-2 text-[15px] text-gray-700">
          <MapPin
            className="mt-0.5 h-5 w-5 shrink-0"
            style={{ color: brand }}
          />
          {school.address}
        </p>
      )}
      {hasCoords && (
        <>
          <div className="overflow-hidden rounded-3xl border border-gray-200">
            <iframe
              title="Mapa da escola"
              className="h-80 w-full md:h-96"
              loading="lazy"
              src={`https://www.google.com/maps?q=${school.latitude},${school.longitude}&z=15&output=embed`}
            />
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <OutlineLink
              href={`https://www.google.com/maps/search/?api=1&query=${school.latitude},${school.longitude}`}
            >
              <MapPin className="h-4 w-4" /> Abrir no Google Maps
            </OutlineLink>
            <OutlineLink
              href={`https://waze.com/ul?ll=${school.latitude},${school.longitude}&navigate=yes`}
            >
              <Navigation className="h-4 w-4" /> Abrir no Waze
            </OutlineLink>
          </div>
        </>
      )}
    </SectionShell>
  )
}

/* ─────────────────────────── FAQ ─────────────────────────── */

/* ─────────────────────────── Card flutuante ─────────────────────────── */

function BookingCard({
  school,
  brand,
  minPrice,
  whatsappUrl,
  onRegister,
}: {
  school: School
  brand: string
  minPrice: number | null
  whatsappUrl: string | null
  onRegister: () => void
}) {
  // Página de criador: cartão de contato, não de matrícula — não tem pacote,
  // não tem app, não tem taxa de matrícula. Sem isto o card fingia um fluxo
  // de compra que não existe (o "Matricule-se" cairia como heading vazio).
  if (school.kind === 'creator_page') {
    const social = school.social ?? {}
    const socialLinks = [
      social.website && { href: social.website, label: 'Site', Icon: Link2 },
      social.instagram && {
        href: social.instagram,
        label: 'Instagram',
        Icon: Instagram,
      },
    ].filter(
      (link): link is { href: string; label: string; Icon: LucideIcon } =>
        Boolean(link),
    )

    return (
      <div className="rounded-3xl border border-gray-200 bg-white p-6 shadow-[0_6px_28px_rgba(0,0,0,0.08)]">
        <p className="text-sm text-gray-500">Fale direto com</p>
        <p className="text-xl font-semibold text-gray-900">{school.name}</p>

        {whatsappUrl && (
          <WhatsAppLink
            href={whatsappUrl}
            source="contact_card"
            listingType={school.kind}
            listingSlug={school.slug}
            className="mt-5 flex w-full items-center justify-center gap-2 rounded-full py-3.5 text-sm font-semibold text-white shadow-sm transition-opacity hover:opacity-90"
            style={{ backgroundColor: brand }}
          >
            <Phone className="h-4 w-4" aria-hidden="true" /> Falar no WhatsApp
          </WhatsAppLink>
        )}

        {socialLinks.length > 0 && (
          <ul className="mt-4 space-y-2 border-t border-gray-100 pt-4 text-sm">
            {socialLinks.map(({ href, label, Icon }) => (
              <li key={label}>
                <a
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-gray-700 transition-colors hover:text-gray-900"
                >
                  <Icon
                    className="h-4 w-4"
                    style={{ color: brand }}
                    aria-hidden="true"
                  />
                  {label}
                </a>
              </li>
            ))}
          </ul>
        )}
      </div>
    )
  }

  return (
    <div className="rounded-3xl border border-gray-200 bg-white p-6 shadow-[0_6px_28px_rgba(0,0,0,0.08)]">
      <div className="flex items-baseline gap-1.5">
        {minPrice != null ? (
          <>
            <span className="text-sm text-gray-500">a partir de</span>
            <span className="text-2xl font-bold text-gray-900">
              {formatPrice(minPrice)}
            </span>
          </>
        ) : (
          <span className="text-xl font-semibold text-gray-900">
            Matricule-se
          </span>
        )}
      </div>
      {minPrice != null && (
        <p className="text-xs text-gray-400">por pacote de aulas</p>
      )}

      {school.registration_enabled && (
        <button
          type="button"
          onClick={onRegister}
          className="mt-5 w-full rounded-full py-3.5 text-sm font-semibold text-white shadow-sm transition-opacity hover:opacity-90"
          style={{ backgroundColor: brand }}
        >
          Cadastre-se
        </button>
      )}
      {whatsappUrl && (
        <WhatsAppLink
          href={whatsappUrl}
          source="booking_card"
          listingType={school.kind}
          listingSlug={school.slug}
          className="mt-3 flex w-full items-center justify-center gap-2 rounded-full border border-gray-300 py-3.5 text-sm font-semibold text-gray-800 transition-colors hover:bg-gray-50"
        >
          <Phone className="h-4 w-4" /> Falar no WhatsApp
        </WhatsAppLink>
      )}

      {/* Só entra aqui o que vem do cadastro da escola. Nada de selo de
          verificação (não existe esse processo) nem de "cadastro gratuito"
          (contradiz o pacote pago logo acima). */}
      <ul className="mt-5 space-y-3 border-t border-gray-100 pt-5 text-sm text-gray-700">
        <CardPerk icon={Check} brand={brand}>
          {school.requires_enrollment && school.enrollment_price
            ? `Matrícula: ${formatPrice(school.enrollment_price)}`
            : 'Sem taxa de matrícula'}
        </CardPerk>
        <CardPerk icon={Smartphone} brand={brand}>
          Agende as aulas pelo app
        </CardPerk>
      </ul>
    </div>
  )
}

function CardPerk({
  icon: Icon,
  brand,
  children,
}: {
  icon: typeof Check
  brand: string
  children: React.ReactNode
}) {
  return (
    <li className="flex items-center gap-3">
      <Icon className="h-4 w-4 shrink-0" style={{ color: brand }} />
      {children}
    </li>
  )
}

/* ─────────────────────────── Barra mobile ─────────────────────────── */

/**
 * Versão mobile do card de reserva. No desktop ele é um painel fixo na coluna da
 * direita; aqui vira barra flutuante que expande, porque ocupar a tela inteira com
 * ele empurraria o conteúdo da escola para baixo da dobra.
 *
 * Recolhida mostra preço e a ação principal; expandida revela o mesmo BookingCard
 * do desktop — WhatsApp, matrícula, selos — sem duplicar conteúdo.
 */
function MobileBookingBar({
  school,
  brand,
  minPrice,
  whatsappUrl,
  formatPrice,
  onRegister,
}: {
  school: School
  brand: string
  minPrice: number | null
  whatsappUrl: string | null
  formatPrice: (v: number) => string
  onRegister: () => void
}) {
  const [expanded, setExpanded] = useState(false)

  return (
    <>
      {expanded && (
        <button
          type="button"
          aria-label="Fechar detalhes"
          onClick={() => setExpanded(false)}
          className="fixed inset-0 z-40 bg-black/40 lg:hidden"
        />
      )}

      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-gray-200 bg-white shadow-[0_-4px_24px_rgba(0,0,0,0.12)] lg:hidden">
        {expanded && (
          <div className="max-h-[70dvh] overflow-y-auto overscroll-contain px-5 pt-4">
            <BookingCard
              school={school}
              brand={brand}
              minPrice={minPrice}
              whatsappUrl={whatsappUrl}
              onRegister={onRegister}
            />
          </div>
        )}

        <div
          className={cn(
            PUBLIC_CONTAINER,
            'flex items-center justify-between gap-3 py-3',
          )}
        >
          <button
            type="button"
            onClick={() => setExpanded((open) => !open)}
            aria-expanded={expanded}
            className="flex min-w-0 flex-1 items-center gap-2 text-left leading-tight"
          >
            <span className="min-w-0">
              {minPrice != null ? (
                <>
                  <span className="block text-base font-bold text-gray-900">
                    {formatPrice(minPrice)}
                  </span>
                  <span className="block text-xs text-gray-400">
                    por pacote de aulas
                  </span>
                </>
              ) : (
                <span className="block text-sm font-semibold text-gray-900">
                  Ver detalhes
                </span>
              )}
            </span>
            <ChevronUp
              aria-hidden="true"
              className={cn(
                'h-4 w-4 shrink-0 text-gray-400 transition-transform',
                expanded && 'rotate-180',
              )}
            />
          </button>

          {school.registration_enabled ? (
            <button
              type="button"
              onClick={onRegister}
              className="shrink-0 rounded-full px-6 py-3 text-sm font-semibold text-white shadow-sm transition-opacity hover:opacity-90"
              style={{ backgroundColor: brand }}
            >
              Cadastre-se
            </button>
          ) : (
            whatsappUrl && (
              <WhatsAppLink
                href={whatsappUrl}
                source="mobile_bar"
                listingType={school.kind}
                listingSlug={school.slug}
                className="shrink-0 rounded-full px-6 py-3 text-sm font-semibold text-white shadow-sm transition-opacity hover:opacity-90"
                style={{ backgroundColor: brand }}
              >
                WhatsApp
              </WhatsAppLink>
            )
          )}
        </div>
      </div>
    </>
  )
}

/* ─────────────────────────── CTA final ─────────────────────────── */

function FinalCta({
  school,
  brand,
  onRegister,
}: {
  school: School
  brand: string
  onRegister: () => void
}) {
  const bg = school.gallery[1] || school.gallery[0]

  return (
    <section className={cn(PUBLIC_CONTAINER, 'py-10 md:py-16')}>
      <div className="relative overflow-hidden rounded-[28px]">
        {bg ? (
          <img
            src={bg}
            alt=""
            className="absolute inset-0 h-full w-full object-cover"
          />
        ) : (
          <div
            className="absolute inset-0"
            style={{
              background: `linear-gradient(135deg, ${brand}, ${school.secondary_color || brand})`,
            }}
          />
        )}
        {/* Gradiente para legibilidade */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/45 to-black/25" />

        <div className="relative flex flex-col items-center px-6 py-20 text-center text-white md:py-28">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-xs font-medium backdrop-blur">
            <Waves className="h-3.5 w-3.5" /> {school.name}
          </span>
          <h2 className="mt-5 max-w-2xl text-3xl font-bold leading-tight tracking-tight md:text-[42px]">
            Pronto para pegar sua primeira onda?
          </h2>
          <p className="mt-4 max-w-xl text-base text-white/90 md:text-lg">
            Crie sua conta em menos de um minuto e agende suas aulas de surf
            pelo app Bewave.
          </p>
          <button
            type="button"
            onClick={onRegister}
            className="mt-9 inline-flex items-center gap-2 rounded-full bg-white px-8 py-4 text-sm font-semibold shadow-lg transition-transform hover:scale-[1.03] md:text-base"
            style={{ color: brand }}
          >
            Cadastre-se gratuitamente{' '}
            <ArrowRight aria-hidden="true" className="h-5 w-5" />
          </button>
          <div className="mt-7 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-white/85">
            <span className="inline-flex items-center gap-1.5">
              <Smartphone className="h-4 w-4" /> Agende pelo app
            </span>
          </div>
        </div>
      </div>
    </section>
  )
}

/* ─────────────────────────── Footer ─────────────────────────── */

// As colunas desta escola entram à esquerda das da plataforma; o resto do rodapé
// — marca, links da BeWave, redes, legais — vem igual ao da landing e do diretório.
function Footer({
  school,
  whatsappUrl,
}: {
  school: School
  whatsappUrl: string | null
}) {
  const contactLinks = [
    whatsappUrl && {
      label: 'WhatsApp',
      href: whatsappUrl,
      icon: Phone,
      external: true,
      onClick: () =>
        trackWhatsAppClick({
          source: 'footer',
          listing_type: school.kind,
          listing_slug: school.slug,
        }),
    },
    (school.social?.instagram || school.instagram_url) && {
      label: 'Instagram',
      href: (school.social?.instagram || school.instagram_url) as string,
      icon: Instagram,
      external: true,
    },
  ].filter(Boolean) as FooterLink[]

  const isCreatorPage = school.kind === 'creator_page'
  const columns: FooterColumn[] = [
    {
      title: 'Navegue',
      links: [
        { label: isCreatorPage ? 'Sobre' : 'Sobre a escola', href: '#sobre' },
        ...(isCreatorPage
          ? []
          : [{ label: 'Aulas e pacotes', href: '#pacotes' }]),
        { label: 'Localização', href: '#localizacao' },
      ],
    },
  ]

  if (contactLinks.length > 0) {
    columns.push({ title: 'Contato', links: contactLinks })
  }

  return (
    <BewaveFooter
      extraColumns={columns}
      // Espaço para a barra de reserva fixa no rodapé.
      clearsFixedBar
    />
  )
}

/* ─────────────────────────── UI compartilhada ─────────────────────────── */

function Divider() {
  return <hr className="my-9 border-gray-200 md:my-11" />
}

/**
 * Faixa de largura total. Só funciona fora do grid de 2 colunas do corpo — lá
 * dentro a seção fica presa à coluna da esquerda e o fundo não alcança as bordas.
 */
function Band({
  children,
  tone = 'gray',
}: {
  children: ReactNode
  tone?: 'gray' | 'white'
}) {
  // `gray` é o próprio fundo da página; a alternância acontece quando uma faixa
  // sobe para branco. Antes era o contrário — página branca, faixa cinza.
  return (
    <div className={tone === 'gray' ? 'bg-bewave-surface' : 'bg-white'}>
      <div className={cn(PUBLIC_CONTAINER, 'py-12')}>{children}</div>
    </div>
  )
}

function SectionShell({
  title,
  children,
  id,
}: {
  title: React.ReactNode
  children: React.ReactNode
  id?: string
}) {
  return (
    <section id={id} className="scroll-mt-24">
      <h2 className="mb-6 text-xl font-semibold tracking-tight text-gray-900 md:text-[22px]">
        {title}
      </h2>
      {children}
    </section>
  )
}

/**
 * Trilho horizontal com rolagem nativa — mesmo mecanismo do carrossel de chips
 * dos filtros (`FeatureChips`): scroll + snap do navegador, sem biblioteca. As
 * setas só existem quando sobra o que rolar; com poucos itens a fila cabe
 * inteira e elas nem aparecem.
 */
function Rail({
  children,
  ariaLabel,
}: {
  children: ReactNode
  ariaLabel: string
}) {
  const trackRef = useRef<HTMLDivElement>(null)
  const [atStart, setAtStart] = useState(true)
  const [atEnd, setAtEnd] = useState(true)

  const syncEdges = useCallback(() => {
    const track = trackRef.current
    if (!track) return

    setAtStart(track.scrollLeft <= 1)
    setAtEnd(track.scrollLeft + track.clientWidth >= track.scrollWidth - 1)
  }, [])

  useEffect(() => {
    syncEdges()
    const track = trackRef.current
    if (!track) return

    const observer = new ResizeObserver(syncEdges)
    observer.observe(track)
    return () => observer.disconnect()
  }, [syncEdges])

  const scrollByPage = (direction: 1 | -1) => {
    const track = trackRef.current
    if (!track) return

    track.scrollBy({
      left: direction * Math.max(240, track.clientWidth * 0.8),
      behavior: 'smooth',
    })
  }

  return (
    <div className="relative">
      <div
        ref={trackRef}
        onScroll={syncEdges}
        // No desktop a roda do mouse só rola na vertical, e a barra está
        // escondida: sem converter o movimento, a fila simplesmente não anda.
        onWheel={(event) => {
          if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return
          event.currentTarget.scrollLeft += event.deltaY
        }}
        role="list"
        aria-label={ariaLabel}
        className="flex snap-x snap-mandatory gap-4 overflow-x-auto pb-2 [-webkit-overflow-scrolling:touch] [scrollbar-width:none] [touch-action:pan-x] [&::-webkit-scrollbar]:hidden"
      >
        {children}
      </div>

      {!atStart && (
        <button
          type="button"
          aria-label="Ver anteriores"
          title="Ver anteriores"
          onClick={() => scrollByPage(-1)}
          className="absolute left-0 top-1/2 hidden size-9 -translate-y-1/2 items-center justify-center rounded-full border bg-white shadow-md transition-colors hover:bg-gray-50 lg:flex"
        >
          <ChevronLeft aria-hidden="true" className="h-4 w-4" />
        </button>
      )}
      {!atEnd && (
        <button
          type="button"
          aria-label="Ver mais"
          title="Ver mais"
          onClick={() => scrollByPage(1)}
          className="absolute right-0 top-1/2 hidden size-9 -translate-y-1/2 items-center justify-center rounded-full border bg-white shadow-md transition-colors hover:bg-gray-50 lg:flex"
        >
          <ChevronRight aria-hidden="true" className="h-4 w-4" />
        </button>
      )}
    </div>
  )
}

function OutlineLink({
  href,
  children,
}: {
  href: string
  children: React.ReactNode
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1.5 rounded-full border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-800 transition-colors hover:bg-gray-50"
    >
      {children}
    </a>
  )
}

/* ─────────────────────────── Dialog de cadastro ─────────────────────────── */

function RegisterDialog({
  open,
  onOpenChange,
  slug,
  brand,
  onSuccess,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  slug: string
  brand: string
  onSuccess: () => void
}) {
  const { data, setData, post, processing, errors, reset } = useForm({
    name: '',
    email: '',
    document: '',
    phone: '',
    password: '',
    password_confirmation: '',
  })

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    post(`/${slug}/register`, {
      preserveScroll: true,
      onSuccess: () => {
        reset()
        onSuccess()
      },
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Criar minha conta</DialogTitle>
          <DialogDescription>
            Preencha seus dados para entrar na escola. Depois é só acessar pelo
            app Bewave com seu CPF e senha.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-3">
          <Field label="Nome completo" error={errors.name}>
            <TextInput
              value={data.name}
              onChange={(v) => setData('name', v)}
              placeholder="Seu nome"
            />
          </Field>
          <Field label="Email" error={errors.email}>
            <TextInput
              type="email"
              value={data.email}
              onChange={(v) => setData('email', v)}
              placeholder="voce@email.com"
            />
          </Field>
          <Field label="CPF" error={errors.document}>
            <TextInput
              value={data.document}
              onChange={(v) => setData('document', formatCpf(v))}
              placeholder="000.000.000-00"
              inputMode="numeric"
            />
          </Field>
          <Field label="Telefone" error={errors.phone}>
            <TextInput
              value={data.phone}
              onChange={(v) => setData('phone', formatPhone(v))}
              placeholder="(85) 99999-9999"
              inputMode="numeric"
            />
          </Field>
          <Field label="Senha" error={errors.password}>
            <TextInput
              type="password"
              value={data.password}
              onChange={(v) => setData('password', v)}
              placeholder="Mínimo 8 caracteres"
            />
          </Field>
          <Field label="Confirmar senha" error={errors.password_confirmation}>
            <TextInput
              type="password"
              value={data.password_confirmation}
              onChange={(v) => setData('password_confirmation', v)}
              placeholder="Repita a senha"
            />
          </Field>

          <button
            type="submit"
            disabled={processing}
            className="mt-2 w-full rounded-full py-3 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-60"
            style={{ backgroundColor: brand }}
          >
            {processing ? 'Criando conta...' : 'Criar minha conta'}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function TextInput({
  value,
  onChange,
  type = 'text',
  placeholder,
  inputMode,
}: {
  value: string
  onChange: (v: string) => void
  type?: string
  placeholder?: string
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode']
}) {
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      inputMode={inputMode}
      className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none transition-colors focus:border-gray-400"
    />
  )
}

function Field({
  label,
  error,
  children,
}: {
  label: string
  error?: string | string[]
  children: React.ReactNode
}) {
  const message = Array.isArray(error) ? error[0] : error
  return (
    <div className="space-y-1">
      <label className="text-sm font-medium text-gray-700">{label}</label>
      {children}
      {message && <p className="text-xs text-red-600">{message}</p>}
    </div>
  )
}

function SuccessDialog({
  open,
  onOpenChange,
  brand,
  schoolName,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  brand: string
  schoolName: string
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div
            className="mx-auto mb-2 flex h-14 w-14 items-center justify-center rounded-full"
            style={{ backgroundColor: `${brand}14`, color: brand }}
          >
            <CheckCircle2 className="h-8 w-8" />
          </div>
          <DialogTitle className="text-center text-xl">
            Cadastro realizado! 🎉
          </DialogTitle>
          <DialogDescription className="text-center">
            Sua conta na {schoolName} foi criada. Agora baixe o app Bewave e
            entre com seu <strong>CPF</strong> e a senha que você acabou de
            criar para agendar suas aulas.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-2 flex flex-col gap-3">
          <p className="flex items-center justify-center gap-2 text-sm font-medium text-gray-500">
            <Smartphone className="h-4 w-4" style={{ color: brand }} /> Baixe o
            app Bewave
          </p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <StoreButton
              href={APP_STORE_URL}
              store="App Store"
              hint="para iPhone"
            />
            <StoreButton
              href={PLAY_STORE_URL}
              store="Google Play"
              hint="para Android"
            />
          </div>
        </div>

        <button
          type="button"
          onClick={() => onOpenChange(false)}
          className="mt-2 w-full rounded-full py-3 text-sm font-semibold text-white transition-opacity hover:opacity-90"
          style={{ backgroundColor: brand }}
        >
          Entendi
        </button>
      </DialogContent>
    </Dialog>
  )
}

function StoreButton({
  href,
  store,
  hint,
}: {
  href: string
  store: string
  hint: string
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center justify-center gap-2 rounded-xl border border-gray-200 px-4 py-3 text-center transition-colors hover:bg-gray-50"
    >
      <div className="leading-tight">
        <p className="text-[11px] text-gray-400">{hint}</p>
        <p className="text-sm font-semibold text-gray-800">{store}</p>
      </div>
    </a>
  )
}
